import {
  Decoration,
  JumpPad,
  LadderZone,
  LevelBox,
  MovingPlatform,
  PickupSpawn,
  ShootingLane,
  TrainingTarget,
  Vec3,
} from "./protocol.ts";

export interface LevelPropPlacement {
  key: string;
  pos: Vec3;
  scale: number;
  yaw: number;
}

export interface LevelDefinition {
  id: string;
  name: string;
  boxes: LevelBox[];
  spawnPoints: Vec3[];
  pickupSpawns: PickupSpawn[];
  jumpPads: JumpPad[];
  movingPlatforms: MovingPlatform[];
  ladders: LadderZone[];
  decorations: Decoration[];
  propPlacements: LevelPropPlacement[];
  trainingTargets: TrainingTarget[];
  balloonSpawns: Vec3[];
  shootingLanes: ShootingLane[];
}

const FLOOR = 0xb98f5b;
const WALL = 0x475569;
const COVER = 0x334155;

const arena: LevelDefinition = {
  id: "arena",
  name: "Ascent Arena",
  boxes: [
    { min: { x: -12, y: -0.5, z: -12 }, max: { x: 12, y: 0, z: 12 }, color: FLOOR },
    { min: { x: -34, y: -0.5, z: -12 }, max: { x: -12, y: 0, z: 12 }, color: FLOOR },
    { min: { x: 12, y: -0.5, z: -12 }, max: { x: 34, y: 0, z: 12 }, color: FLOOR },
    { min: { x: -12, y: -0.5, z: -34 }, max: { x: 12, y: 0, z: -12 }, color: FLOOR },
    { min: { x: -12, y: -0.5, z: 12 }, max: { x: 12, y: 0, z: 34 }, color: FLOOR },
    { min: { x: -34, y: -0.5, z: -34 }, max: { x: -12, y: 0, z: -12 }, color: FLOOR },
    { min: { x: 12, y: -0.5, z: -34 }, max: { x: 34, y: 0, z: -12 }, color: FLOOR },
    { min: { x: -34, y: -0.5, z: 12 }, max: { x: -12, y: 0, z: 34 }, color: FLOOR },
    { min: { x: 12, y: -0.5, z: 12 }, max: { x: 34, y: 0, z: 34 }, color: FLOOR },
    { min: { x: -34.5, y: 0, z: -34.5 }, max: { x: -34, y: 10, z: 34.5 }, color: WALL },
    { min: { x: 34, y: 0, z: -34.5 }, max: { x: 34.5, y: 10, z: 34.5 }, color: WALL },
    { min: { x: -34.5, y: 0, z: -34.5 }, max: { x: 34.5, y: 10, z: -34 }, color: WALL },
    { min: { x: -34.5, y: 0, z: 34 }, max: { x: 34.5, y: 10, z: 34.5 }, color: WALL },
    { min: { x: -30, y: 3.5, z: -8 }, max: { x: -20, y: 4, z: 10 }, color: FLOOR },
    { min: { x: 20, y: 5, z: -12 }, max: { x: 32, y: 5.5, z: 6 }, color: FLOOR },
    { min: { x: -8, y: 3, z: -30 }, max: { x: 10, y: 3.5, z: -18 }, color: FLOOR },
    { min: { x: -10, y: 6, z: 20 }, max: { x: 6, y: 6.5, z: 32 }, color: FLOOR },
    { min: { x: 16, y: 7.5, z: 16 }, max: { x: 26, y: 8, z: 28 }, color: FLOOR },
    { min: { x: -12, y: 0, z: -4 }, max: { x: -10.4, y: 0.8, z: 4 }, color: FLOOR },
    { min: { x: -13.6, y: 0, z: -4 }, max: { x: -12, y: 1.6, z: 4 }, color: FLOOR },
    { min: { x: -15.2, y: 0, z: -4 }, max: { x: -13.6, y: 2.4, z: 4 }, color: FLOOR },
    { min: { x: -16.8, y: 0, z: -4 }, max: { x: -15.2, y: 3.2, z: 4 }, color: FLOOR },
    { min: { x: -18.4, y: 0, z: -4 }, max: { x: -16.8, y: 4, z: 4 }, color: FLOOR },
    { min: { x: -20, y: 0, z: -4 }, max: { x: -18.4, y: 4, z: 4 }, color: FLOOR },
    { min: { x: 12, y: 0, z: -6 }, max: { x: 13.15, y: 0.785, z: 2 }, color: FLOOR },
    { min: { x: 13.15, y: 0, z: -7 }, max: { x: 14.3, y: 1.57, z: 1 }, color: FLOOR },
    { min: { x: 14.3, y: 0, z: -8 }, max: { x: 15.45, y: 2.355, z: 0 }, color: FLOOR },
    { min: { x: 15.45, y: 0, z: -9 }, max: { x: 16.6, y: 3.14, z: -1 }, color: FLOOR },
    { min: { x: 16.6, y: 0, z: -10 }, max: { x: 17.75, y: 3.925, z: -2 }, color: FLOOR },
    { min: { x: 17.75, y: 0, z: -11 }, max: { x: 18.9, y: 4.71, z: -3 }, color: FLOOR },
    { min: { x: 18.9, y: 0, z: -12 }, max: { x: 20, y: 5.5, z: -4 }, color: FLOOR },
    { min: { x: -4, y: 0, z: -12 }, max: { x: 4, y: 0.875, z: -10 }, color: FLOOR },
    { min: { x: -4, y: 0, z: -14 }, max: { x: 4, y: 1.75, z: -12 }, color: FLOOR },
    { min: { x: -4, y: 0, z: -16 }, max: { x: 4, y: 2.625, z: -14 }, color: FLOOR },
    { min: { x: -4, y: 0, z: -18 }, max: { x: 4, y: 3.5, z: -16 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 12 }, max: { x: 4, y: 0.8125, z: 13 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 13 }, max: { x: 4, y: 1.625, z: 14 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 14 }, max: { x: 4, y: 2.4375, z: 15 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 15 }, max: { x: 4, y: 3.25, z: 16 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 16 }, max: { x: 4, y: 4.0625, z: 17 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 17 }, max: { x: 4, y: 4.875, z: 18 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 18 }, max: { x: 4, y: 5.6875, z: 19 }, color: FLOOR },
    { min: { x: -4, y: 0, z: 19 }, max: { x: 4, y: 6.5, z: 20 }, color: FLOOR },
    { min: { x: 12, y: 0, z: 18 }, max: { x: 13.15, y: 0.866, z: 19.1 }, color: FLOOR },
    { min: { x: 13.15, y: 0, z: 19.1 }, max: { x: 14.3, y: 1.732, z: 20.2 }, color: FLOOR },
    { min: { x: 14.3, y: 0, z: 20.2 }, max: { x: 15.45, y: 2.598, z: 21.3 }, color: FLOOR },
    { min: { x: 15.45, y: 0, z: 21.3 }, max: { x: 16.6, y: 3.464, z: 22.4 }, color: FLOOR },
    { min: { x: 16.6, y: 0, z: 22.4 }, max: { x: 17.75, y: 4.33, z: 23.5 }, color: FLOOR },
    { min: { x: 17.75, y: 0, z: 23.5 }, max: { x: 18.9, y: 5.196, z: 24.6 }, color: FLOOR },
    { min: { x: 18.9, y: 0, z: 24.6 }, max: { x: 20.05, y: 6.062, z: 25.7 }, color: FLOOR },
    { min: { x: 20.05, y: 0, z: 25.7 }, max: { x: 21.2, y: 6.928, z: 26.8 }, color: FLOOR },
    { min: { x: 21.2, y: 0, z: 26.8 }, max: { x: 22.35, y: 7.794, z: 27.9 }, color: FLOOR },
    { min: { x: -4, y: 9.5, z: -4 }, max: { x: 4, y: 10, z: 4 }, color: FLOOR },
    { min: { x: -9, y: 0, z: -3 }, max: { x: -6, y: 2.6, z: 3 }, color: COVER },
    { min: { x: 7, y: 0, z: -8 }, max: { x: 10, y: 3.4, z: -2 }, color: COVER },
    { min: { x: -3, y: 0, z: -9 }, max: { x: 3, y: 2.2, z: -6 }, color: COVER },
    { min: { x: -4, y: 0, z: 6 }, max: { x: 2, y: 2.8, z: 9 }, color: COVER },
    { min: { x: -20, y: 0, z: -22 }, max: { x: -16, y: 4.5, z: -18 }, color: COVER },
    { min: { x: 23, y: 0, z: 19 }, max: { x: 27, y: 8.5, z: 23 }, color: COVER },
    { min: { x: -28, y: 0, z: 16 }, max: { x: -24, y: 3.8, z: 22 }, color: COVER },
    { min: { x: 24, y: 0, z: -28 }, max: { x: 28, y: 4.2, z: -22 }, color: COVER },
    { min: { x: -18, y: 0, z: 4 }, max: { x: -8, y: 6, z: 4.5 }, color: WALL },
    { min: { x: 8, y: 0, z: -18 }, max: { x: 8.5, y: 7, z: -6 }, color: WALL },
    { min: { x: -6, y: 0, z: 22 }, max: { x: 6, y: 6.5, z: 22.5 }, color: WALL },
    { min: { x: 18, y: 0, z: 8 }, max: { x: 18.5, y: 7.5, z: 18 }, color: WALL },
  ],
  spawnPoints: [
    { x: -28, y: 0.05, z: -26 },
    { x: 28, y: 0.05, z: -28 },
    { x: -30, y: 0.05, z: 26 },
    { x: 30, y: 0.05, z: 30 },
    { x: -24, y: 4.05, z: 2 },
    { x: 26, y: 5.55, z: -2 },
    { x: 0, y: 3.55, z: -24 },
    { x: -2, y: 6.55, z: 26 },
  ],
  pickupSpawns: [
    {
      id: "rifle-mid",
      type: "weapon",
      weaponId: "rifle",
      label: "RIFLE",
      pos: { x: 0, y: 0.5, z: 0 },
      respawnMs: 12000,
    },
    {
      id: "shotgun-west",
      type: "weapon",
      weaponId: "shotgun",
      label: "SHOTGUN",
      pos: { x: -24, y: 4.3, z: 1 },
      respawnMs: 15000,
    },
    {
      id: "sniper-east",
      type: "weapon",
      weaponId: "sniper",
      label: "SNIPER",
      pos: { x: 26, y: 5.8, z: -3 },
      respawnMs: 18000,
    },
    {
      id: "rifle-north",
      type: "weapon",
      weaponId: "rifle",
      label: "RIFLE",
      pos: { x: 1, y: 3.8, z: -24 },
      respawnMs: 12000,
    },
    {
      id: "sniper-tower",
      type: "weapon",
      weaponId: "sniper",
      label: "SNIPER",
      pos: { x: 21, y: 8.3, z: 22 },
      respawnMs: 20000,
    },
    {
      id: "shotgun-south",
      type: "weapon",
      weaponId: "shotgun",
      label: "SHOTGUN",
      pos: { x: -2, y: 6.8, z: 27 },
      respawnMs: 15000,
    },
    {
      id: "ammo-west",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: -24, y: 0.5, z: -12 },
      respawnMs: 8000,
    },
    {
      id: "ammo-east",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: 24, y: 0.5, z: 12 },
      respawnMs: 8000,
    },
    {
      id: "ammo-north",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: -10, y: 0.5, z: -24 },
      respawnMs: 8000,
    },
    {
      id: "ammo-south",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: 10, y: 0.5, z: 24 },
      respawnMs: 8000,
    },
  ],
  jumpPads: [
    {
      min: { x: -1.2, y: 0, z: -1.2 },
      max: { x: 1.2, y: 0.25, z: 1.2 },
      force: 22,
      color: 0x38bdf8,
    },
    {
      min: { x: -29.2, y: 0, z: 25.8 },
      max: { x: -26.8, y: 0.25, z: 28.2 },
      force: 14,
      color: 0x22d3ee,
    },
    {
      min: { x: 27.8, y: 0, z: -29.2 },
      max: { x: 30.2, y: 0.25, z: -26.8 },
      force: 14,
      color: 0x22d3ee,
    },
  ],
  movingPlatforms: [
    {
      id: "west-lift",
      size: { x: 3.2, y: 0.45, z: 3.2 },
      from: { x: -10, y: 1.4, z: 18 },
      to: { x: -10, y: 4.4, z: 18 },
      periodMs: 2800,
      color: 0xf97316,
    },
    {
      id: "tower-shuttle",
      size: { x: 3.4, y: 0.45, z: 3.4 },
      from: { x: 10, y: 4.7, z: 24 },
      to: { x: 18.5, y: 7.6, z: 24 },
      periodMs: 3000,
      color: 0x38bdf8,
    },
  ],
  ladders: [],
  decorations: [
    { id: "light-nw", kind: "light", pos: { x: -31, y: 0, z: -31 }, color: 0xfacc15 },
    { id: "light-ne", kind: "light", pos: { x: 31, y: 0, z: -31 }, color: 0xfacc15 },
    { id: "light-sw", kind: "light", pos: { x: -31, y: 0, z: 31 }, color: 0xfacc15 },
    { id: "light-se", kind: "light", pos: { x: 31, y: 0, z: 31 }, color: 0xfacc15 },
    { id: "crate-west", kind: "crate", pos: { x: -21, y: 4, z: 8 }, color: 0x38bdf8 },
    { id: "crate-north", kind: "crate", pos: { x: 8, y: 3.5, z: -25 }, color: 0x38bdf8 },
    { id: "obelisk-south", kind: "obelisk", pos: { x: -7, y: 6.5, z: 28 }, color: 0xa78bfa },
    { id: "obelisk-east", kind: "obelisk", pos: { x: 24, y: 8, z: 26 }, color: 0xa78bfa },
    { id: "antenna-west", kind: "antenna", pos: { x: -27, y: 4, z: -2 }, color: 0x38bdf8 },
    { id: "antenna-tower", kind: "antenna", pos: { x: 24, y: 8, z: 18 }, color: 0x38bdf8 },
  ],
  propPlacements: [
    { key: "banner", pos: { x: -33.2, y: 0, z: 0 }, scale: 2.2, yaw: Math.PI * 0.5 },
    { key: "banner", pos: { x: 33.2, y: 0, z: 0 }, scale: 2.2, yaw: -Math.PI * 0.5 },
    { key: "banner", pos: { x: 0, y: 0, z: -33.2 }, scale: 2.2, yaw: 0 },
    { key: "banner", pos: { x: 0, y: 0, z: 33.2 }, scale: 2.2, yaw: Math.PI },
    { key: "scifi-crate", pos: { x: -24, y: 4, z: 8 }, scale: 1.7, yaw: 0.25 },
    { key: "scifi-crate", pos: { x: 7, y: 3.5, z: -24 }, scale: 1.7, yaw: -0.3 },
    { key: "column", pos: { x: -28, y: 4, z: -7 }, scale: 2.0, yaw: 0 },
    { key: "column", pos: { x: 29, y: 5.5, z: 4 }, scale: 2.0, yaw: 0 },
    { key: "statue", pos: { x: -7, y: 6.5, z: 29 }, scale: 2.1, yaw: Math.PI * 0.2 },
    { key: "statue", pos: { x: 24, y: 8, z: 26 }, scale: 2.1, yaw: -Math.PI * 0.25 },
  ],
  trainingTargets: [],
  balloonSpawns: [],
  shootingLanes: [],
};

const test: LevelDefinition = {
  id: "test",
  name: "Test Yard",
  boxes: [
    { min: { x: -40, y: -0.5, z: -40 }, max: { x: 40, y: 0, z: 40 }, color: FLOOR },
    { min: { x: -40.5, y: 0, z: -40.5 }, max: { x: -40, y: 8, z: 40.5 }, color: WALL },
    { min: { x: 40, y: 0, z: -40.5 }, max: { x: 40.5, y: 8, z: 40.5 }, color: WALL },
    { min: { x: -40.5, y: 0, z: -40.5 }, max: { x: 40.5, y: 8, z: -40 }, color: WALL },
    { min: { x: -40.5, y: 0, z: 40 }, max: { x: 40.5, y: 8, z: 40.5 }, color: WALL },
    { min: { x: -18, y: 0, z: -18 }, max: { x: -2, y: 4, z: -2 }, color: FLOOR },
    { min: { x: 6, y: 0, z: -24 }, max: { x: 24, y: 6, z: -8 }, color: FLOOR },
    { min: { x: -10, y: 0, z: 12 }, max: { x: 12, y: 8, z: 26 }, color: FLOOR },
    { min: { x: 20, y: 0, z: 16 }, max: { x: 32, y: 10, z: 30 }, color: FLOOR },
    { min: { x: -30, y: 0, z: 18 }, max: { x: -18, y: 5, z: 28 }, color: FLOOR },
    { min: { x: -18, y: 0, z: 4 }, max: { x: -12, y: 2.2, z: 12 }, color: COVER },
    { min: { x: -4, y: 0, z: -32 }, max: { x: 4, y: 3, z: -24 }, color: COVER },
    { min: { x: 14, y: 0, z: 2 }, max: { x: 18, y: 5, z: 10 }, color: COVER },
    { min: { x: 28, y: 0, z: -2 }, max: { x: 32, y: 7, z: 6 }, color: COVER },
    { min: { x: -20, y: 0, z: -2 }, max: { x: -8, y: 0.9, z: 0 }, color: FLOOR },
    { min: { x: -20, y: 0, z: 0 }, max: { x: -8, y: 1.8, z: 2 }, color: FLOOR },
    { min: { x: -20, y: 0, z: 2 }, max: { x: -8, y: 2.7, z: 4 }, color: FLOOR },
    { min: { x: -20, y: 0, z: 4 }, max: { x: -8, y: 3.6, z: 6 }, color: FLOOR },
  ],
  spawnPoints: [
    { x: -34, y: 0.05, z: -34 },
    { x: 34, y: 0.05, z: -34 },
    { x: -34, y: 0.05, z: 34 },
    { x: 34, y: 0.05, z: 34 },
    { x: -10, y: 4.05, z: -10 },
    { x: 14, y: 6.05, z: -16 },
    { x: 0, y: 8.05, z: 20 },
    { x: 26, y: 10.05, z: 24 },
  ],
  pickupSpawns: [
    {
      id: "pistol-lane",
      type: "weapon",
      weaponId: "pistol",
      label: "PISTOL",
      pos: { x: -26, y: 0.5, z: 0 },
      respawnMs: 10000,
    },
    {
      id: "rifle-pad",
      type: "weapon",
      weaponId: "rifle",
      label: "RIFLE",
      pos: { x: 0, y: 0.5, z: -12 },
      respawnMs: 12000,
    },
    {
      id: "shotgun-box",
      type: "weapon",
      weaponId: "shotgun",
      label: "SHOTGUN",
      pos: { x: -22, y: 5.3, z: 22 },
      respawnMs: 15000,
    },
    {
      id: "sniper-top",
      type: "weapon",
      weaponId: "sniper",
      label: "SNIPER",
      pos: { x: 26, y: 10.3, z: 24 },
      respawnMs: 18000,
    },
    {
      id: "ammo-west",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: -34, y: 0.5, z: 0 },
      respawnMs: 8000,
    },
    {
      id: "ammo-east",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: 34, y: 0.5, z: 0 },
      respawnMs: 8000,
    },
    {
      id: "ammo-south",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: 0, y: 0.5, z: 34 },
      respawnMs: 8000,
    },
  ],
  jumpPads: [
    {
      min: { x: -1.2, y: 0, z: -1.2 },
      max: { x: 1.2, y: 0.25, z: 1.2 },
      force: 16,
      color: 0x38bdf8,
    },
    {
      min: { x: 29, y: 0, z: -29 },
      max: { x: 31.5, y: 0.25, z: -26.5 },
      force: 18,
      color: 0x22d3ee,
    },
  ],
  movingPlatforms: [
    {
      id: "test-lift",
      size: { x: 4, y: 0.45, z: 4 },
      from: { x: 8, y: 2, z: 8 },
      to: { x: 8, y: 7, z: 8 },
      periodMs: 2600,
      color: 0xf97316,
    },
    {
      id: "test-bridge",
      size: { x: 4, y: 0.45, z: 4 },
      from: { x: -24, y: 3.5, z: -24 },
      to: { x: -8, y: 3.5, z: -24 },
      periodMs: 3200,
      color: 0x38bdf8,
    },
  ],
  ladders: [],
  decorations: [
    { id: "test-light-a", kind: "light", pos: { x: -36, y: 0, z: -36 }, color: 0xfacc15 },
    { id: "test-light-b", kind: "light", pos: { x: 36, y: 0, z: 36 }, color: 0xfacc15 },
    { id: "test-obelisk", kind: "obelisk", pos: { x: 4, y: 8, z: 24 }, color: 0xa78bfa },
    { id: "test-antenna", kind: "antenna", pos: { x: 30, y: 10, z: 18 }, color: 0x38bdf8 },
  ],
  propPlacements: [
    { key: "banner", pos: { x: -39, y: 0, z: 0 }, scale: 2.3, yaw: Math.PI * 0.5 },
    { key: "banner", pos: { x: 39, y: 0, z: 0 }, scale: 2.3, yaw: -Math.PI * 0.5 },
    { key: "column", pos: { x: -12, y: 4, z: -12 }, scale: 2.1, yaw: 0 },
    { key: "column", pos: { x: 20, y: 6, z: -12 }, scale: 2.1, yaw: 0 },
    { key: "statue", pos: { x: 4, y: 8, z: 24 }, scale: 2.2, yaw: Math.PI * 0.2 },
    { key: "tree", pos: { x: -28, y: 0, z: 30 }, scale: 2.8, yaw: 0.4 },
    { key: "tree", pos: { x: 30, y: 0, z: -28 }, scale: 2.8, yaw: -0.4 },
    { key: "crate", pos: { x: -16, y: 0, z: 10 }, scale: 2.0, yaw: 0.25 },
    { key: "scifi-crate", pos: { x: 16, y: 0, z: 10 }, scale: 1.9, yaw: -0.25 },
  ],
  trainingTargets: [],
  balloonSpawns: [],
  shootingLanes: [],
};

const training: LevelDefinition = {
  id: "training",
  name: "Training Grounds",
  boxes: [
    { min: { x: -60, y: -0.5, z: -40 }, max: { x: 60, y: 0, z: 40 }, color: 0x334155 },
    { min: { x: -60.5, y: 0, z: -40.5 }, max: { x: -60, y: 6, z: 40.5 }, color: 0x1e293b },
    { min: { x: 60, y: 0, z: -40.5 }, max: { x: 60.5, y: 6, z: 40.5 }, color: 0x1e293b },
    { min: { x: -60.5, y: 0, z: -40.5 }, max: { x: 60.5, y: 6, z: -40 }, color: 0x1e293b },
    { min: { x: -60.5, y: 0, z: 40 }, max: { x: 60.5, y: 6, z: 40.5 }, color: 0x1e293b },
    // pistol lane stalls
    { min: { x: -50, y: 0, z: -8 }, max: { x: -45, y: 1.2, z: -4 }, color: 0x475569 },
    { min: { x: -50, y: 0, z: 4 }, max: { x: -45, y: 1.2, z: 8 }, color: 0x475569 },
    { min: { x: -35, y: 0, z: -8 }, max: { x: -30, y: 1.2, z: -4 }, color: 0x475569 },
    { min: { x: -35, y: 0, z: 4 }, max: { x: -30, y: 1.2, z: 8 }, color: 0x475569 },
    { min: { x: -20, y: 0, z: -8 }, max: { x: -15, y: 1.2, z: -4 }, color: 0x475569 },
    { min: { x: -20, y: 0, z: 4 }, max: { x: -15, y: 1.2, z: 8 }, color: 0x475569 },
    // rifle long range platform
    { min: { x: 10, y: 0, z: -35 }, max: { x: 50, y: 0.3, z: -25 }, color: 0x475569 },
    // elevated target gallery
    { min: { x: 15, y: 0, z: 10 }, max: { x: 55, y: 4, z: 14 }, color: 0x475569 },
    { min: { x: 15, y: 4, z: 10 }, max: { x: 55, y: 4.3, z: 14 }, color: 0x64748b },
    // balloon spawn booth
    { min: { x: -58, y: 0, z: 20 }, max: { x: -50, y: 3, z: 28 }, color: 0x475569 },
  ],
  spawnPoints: [
    { x: 0, y: 0.05, z: 0 },
    { x: -40, y: 0.05, z: 0 },
    { x: 30, y: 0.05, z: -30 },
    { x: 35, y: 4.05, z: 12 },
  ],
  pickupSpawns: [
    {
      id: "pistol-start",
      type: "weapon",
      weaponId: "pistol",
      label: "PISTOL",
      pos: { x: -47.5, y: 0.5, z: -6 },
      respawnMs: 5000,
    },
    {
      id: "rifle-range",
      type: "weapon",
      weaponId: "rifle",
      label: "RIFLE",
      pos: { x: 30, y: 0.5, z: -30 },
      respawnMs: 8000,
    },
    {
      id: "sniper-range",
      type: "weapon",
      weaponId: "sniper",
      label: "SNIPER",
      pos: { x: 48, y: 4.5, z: 12 },
      respawnMs: 10000,
    },
    {
      id: "shotgun-close",
      type: "weapon",
      weaponId: "shotgun",
      label: "SHOTGUN",
      pos: { x: -42, y: 0.5, z: 6 },
      respawnMs: 8000,
    },
    {
      id: "ammo-lane",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: -55, y: 0.5, z: 0 },
      respawnMs: 3000,
    },
    {
      id: "ammo-range",
      type: "ammo",
      label: "AMMO",
      amount: 999,
      pos: { x: 30, y: 0.5, z: -32 },
      respawnMs: 3000,
    },
  ],
  jumpPads: [],
  movingPlatforms: [],
  ladders: [],
  decorations: [
    { id: "light-range", kind: "light", pos: { x: 30, y: 0, z: -30 }, color: 0xfacc15 },
    { id: "light-gallery", kind: "light", pos: { x: 35, y: 4, z: 12 }, color: 0xfacc15 },
    { id: "light-balloons", kind: "light", pos: { x: -54, y: 0, z: 24 }, color: 0x38bdf8 },
  ],
  propPlacements: [
    { key: "crate", pos: { x: -52, y: 0, z: 24 }, scale: 1.6, yaw: 0.3 },
    { key: "crate", pos: { x: -56, y: 0, z: 26 }, scale: 1.4, yaw: -0.2 },
    { key: "column", pos: { x: 20, y: 0, z: -30 }, scale: 1.8, yaw: 0 },
    { key: "column", pos: { x: 40, y: 0, z: -30 }, scale: 1.8, yaw: 0 },
  ],
  trainingTargets: [
    {
      id: "lane-a-10",
      pos: { x: -42.5, y: 1.5, z: -6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "lane-a-25",
      pos: { x: -27.5, y: 1.5, z: -6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "lane-a-40",
      pos: { x: -12.5, y: 1.5, z: -6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "lane-b-10",
      pos: { x: -42.5, y: 1.5, z: 6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: 1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "lane-b-25",
      pos: { x: -27.5, y: 1.5, z: 6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: 1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "lane-b-40",
      pos: { x: -12.5, y: 1.5, z: 6 },
      radius: 0.7,
      normal: { x: 0, y: 0, z: 1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "range-50",
      pos: { x: 30, y: 1.8, z: -24 },
      radius: 1.0,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.6, 1.2, 0.8, 0.4],
    },
    {
      id: "range-100",
      pos: { x: 40, y: 1.8, z: -24 },
      radius: 1.0,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.6, 1.2, 0.8, 0.4],
    },
    {
      id: "range-150",
      pos: { x: 50, y: 1.8, z: -24 },
      radius: 1.0,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.6, 1.2, 0.8, 0.4],
    },
    {
      id: "gallery-1",
      pos: { x: 20, y: 5.5, z: 12 },
      radius: 0.8,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "gallery-2",
      pos: { x: 30, y: 5.5, z: 12 },
      radius: 0.8,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "gallery-3",
      pos: { x: 40, y: 5.5, z: 12 },
      radius: 0.8,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
    {
      id: "gallery-4",
      pos: { x: 50, y: 5.5, z: 12 },
      radius: 0.8,
      normal: { x: 0, y: 0, z: -1 },
      ringDamage: [2, 1.5, 1, 0.5],
    },
  ],
  balloonSpawns: [
    { x: -55, y: 1.2, z: 22 },
    { x: -53, y: 1.2, z: 24 },
    { x: -55, y: 1.2, z: 26 },
    { x: -57, y: 1.2, z: 24 },
    { x: -54, y: 1.2, z: 23 },
    { x: -56, y: 1.2, z: 25 },
  ],
  shootingLanes: [
    { id: "lane-a", from: { x: -55, y: 0.05, z: -6 }, to: { x: -10, y: 0.05, z: -6 }, width: 3 },
    { id: "lane-b", from: { x: -55, y: 0.05, z: 6 }, to: { x: -10, y: 0.05, z: 6 }, width: 3 },
    { id: "range", from: { x: 12, y: 0.05, z: -30 }, to: { x: 55, y: 0.05, z: -30 }, width: 5 },
  ],
};

export const LEVELS = [arena, test, training] as const;
export const DEFAULT_LEVEL_ID = arena.id;

const levelMap = new Map<string, LevelDefinition>(LEVELS.map((level) => [level.id, level]));

export function getLevelData(levelId: string): LevelDefinition {
  return levelMap.get(levelId) ?? arena;
}
